<template>
  <div v-if="show" class="mt-4">
    <v-layout v-if="loading.history" row justify-center pa-4>
      <v-progress-circular indeterminate />
    </v-layout>
    <v-timeline dense class="timeline pa-0" align-top>
      <v-timeline-item
        v-for="(item, index) in activityReservationLogs"
        :key="index"
        small
        fill-dot
        :class="{
          'finished-item': item.finished,
          'creation-item': index + 1 === activityReservationLogs.length
        }"
        :color="activityReservationColorsByStatus[item.finished ? ABSENT : item.current.status].dot"
      >
        <template v-slot:default>
          <template v-if="item.finished">
            <v-layout row class="grey--text">{{ $t(`reservations.finished.${reservation.type}`) }}</v-layout>
          </template>
          <template v-else>
            <v-layout row>
              <span style="font-size: 14px">
                {{ moment(item.created).local().format('DD/MM HH:mm'), }}
              </span>
            </v-layout>
            <v-layout row py-1>
              <span>{{ getTitle(item) }}</span>
              <v-spacer />
              <v-chip v-if="!item.previous || (item.previous.date !== item.current.date || item.previous.time !== item.current.time)" small label>
                <span class="pr-1" :style="item.previous && item.previous.date !== item.current.date ? 'font-weight: bold;' : 'color: grey;'">{{ moment(item.current.date).utc(0).format("DD/MM") }}</span>
                <span :style="item.previous && item.previous.time !== item.current.time ? 'font-weight: bold;' : 'color: grey;'">{{ formatTime(item.current.time) }}</span>
              </v-chip>
            </v-layout>
            <v-layout row>
              <span class="grey--text" style="font-size: 12px">
                {{ item.current.reason }}
              </span>
            </v-layout>
            <v-layout v-if="item.current.status === CANCELLED && order && wasRefunded" row align-center>
              <v-icon color="grey" class="mr-2">mdi-information-outline</v-icon>
              <span class="grey--text" style="font-size: 12px">{{ $t('reservations.refund_emitted') }}</span>
            </v-layout>
          </template>
        </template>
      </v-timeline-item>
    </v-timeline>
    <p
      class="grey--text pt-3 clickable"
      style="font-size: 14px; text-align: center"
      @click="$emit('hideTimeline')"
    >
      {{ $t('reservations.hide_timeline') }}
      <v-icon>arrow_drop_up</v-icon>
    </p>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import moment from 'moment'

import { activityReservationColorsByStatus, activityReservationStatus, reservationTypes } from '@/config'
import { ActivityReservationsRequests } from '@/services/activityReservations.requests'
import { formatTime } from '@/lib/helpers/HoursFormatter'

const { ACCEPTED, PENDING, PENDING_MODIFICATION, ABSENT, CANCELLED } = activityReservationStatus
const activityReservationsService = new ActivityReservationsRequests()

export default {
  components: {},
  props: {
    show: Boolean,
    item: Object,
  },
  data () {
    return {
      moment,
      activityReservationLogs: [],
      activityReservationColorsByStatus,
      formatTime,
      ABSENT,
      loading: {
        history: true,
        transactions: true,
      },
      wasRefunded: null,
      CANCELLED,
    }
  },
  methods: {
    getTitle (item) {
      if (
        item.current.status === ACCEPTED &&
        (item.previous === null ||
          item.previous.status === PENDING)
      ) {
        return this.$te(`reservations.timeline_status.confirmed.${ this.reservation.type }`) ? this.$t(`reservations.timeline_status.confirmed.${ this.reservation.type }`) : this.$t('reservations.timeline_status.confirmed')
      } else if (
        item.current.status === ACCEPTED &&
        item.previous !== null
      ) {
        return this.$te(`reservations.timeline_status.updated.${ this.reservation.type }`) ? this.$t(`reservations.timeline_status.updated.${ this.reservation.type }`) : this.$t('reservations.timeline_status.updated')
      } else if (
        item.current.status === PENDING &&
        item.previous === null
      ) {
        return this.$t('reservations.timeline_status.pending_approval')
      } else if (
        item.current.status === PENDING_MODIFICATION
      ) {
        return this.$t('reservations.timeline_status.requested_modification')
      } else {
        return this.isRoomService && this.$te('reservations.order_status.' + item.current.status)
          ? this.$t('reservations.order_status.' + item.current.status)
          : this.$t('reservations.status.' + item.current.status)
      }
    },
    getHistory () {
      this.loading.history = true
      this.activityReservationLogs = []
      activityReservationsService
        .getHistory(this.reservation.id)
        .then((result) => {
          this.activityReservationLogs = ![ACCEPTED, PENDING, PENDING_MODIFICATION].includes(result[0].current.status)
            ? [{ finished: true }, ...result]
            : result
        })
        .catch(() => {})
        .finally(() => {
          this.loading.history = false
        })
    },
  },
  computed: {
    ...mapState({
      activity: (state) => state.activities.extendedActivity,
    }),
    ...mapGetters({
      accounts: 'me/accounts',
    }),
    reservation () {
      return this.item.reservation
    },
    order () {
      return this.item.order
    },
    isRoomService () {
      return this.reservation.type === reservationTypes.ROOM_SERVICE
    },
  },
  created () {
    if (this.order) {
      this.$store.dispatch('me/getBookings').then(() => {
        this.$store.dispatch('transactions/list', {
          save: true,
          limit: 50,
          offset: 0,
          accountIds: this.accounts.map(({ id }) => id),
          guestId: this.order.guestId,
          stationId: this.order.stationId,
          type: 'BOOKING',
          currencyType: 'LEGAL',
          createdSince: this.order.createdAt,
        }).then(({ list }) => {
          const transaction = list[0] && list[0].transaction
          this.wasRefunded = transaction && transaction.amount && transaction.relatedTransactionId
        }).finally(() =>
          this.loading.transactions = false
        )
      }).catch(() => {
        this.loading.transactions = false
      })
    } else {
      this.loading.transactions = false
    }
  },
  watch: {
    show (value) {
      if (value) {
        this.getHistory()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$dot-size: 20px;
.timeline {
  right: 30px;
}
::v-deep {
  .finished-item {
    .v-timeline-item__dot--small {
      height: 13px !important;
      width: 13px !important;
    }
  }
  .v-timeline-item:not(.finished-item) {
    .v-timeline-item__dot--small {
      height: $dot-size !important;
      width: $dot-size !important;
    }
  }
  .creation-item {
    padding: 0;
    .v-timeline-item__divider {
      &:after {
        content: '';
        position: absolute;
        background-color: white;
        height: 100%;
        width: $dot-size;
      }
    }
  }
}
</style>
